/**
 * Drop / file-input → upload pipeline. Pre-flight scanning, folder
 * creation (mkdir-p semantics), and conflict resolution happen here;
 * the actual byte transfer is delegated to the app-level
 * UploadManagerProvider so uploads survive route changes.
 *
 * Used by both DriveView and WorkspaceExplorer.
 */
import { useCallback } from 'react'
import { useToast } from '@papan/core-ui/ui'
import { collectDroppedFiles, processCollectedDrop, processPickedFiles, type FileToUpload } from '@papan/files/client/dropUpload'
import type { ConflictInfo, ConflictResult, ProgressController } from '@papan/core-ui/ui'
import { useUploadManager, type UploadEntry } from './UploadManagerProvider'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'

type ConflictResolver = (conflict: ConflictInfo) => Promise<ConflictResult>

interface UploadServiceOptions {
  /** Called after all uploads in a batch reach a terminal state */
  onComplete?: (targetFolderId: string | null, successCount: number) => void
}

export function useUploadService(options?: UploadServiceOptions) {
  const toast = useToast()
  const manager = useUploadManager()

  /** Hand off pre-resolved files to the manager. Returns the success count
   *  once every upload reaches a terminal state. */
  const dispatchUploads = useCallback(async (filesToUpload: FileToUpload[], batch: PinnedBatch): Promise<number> => {
    if (filesToUpload.length === 0) return 0
    const promises: Promise<UploadEntry>[] = []
    for (const { file, parentId, conflictStrategy } of filesToUpload) {
      const { promise } = manager.start(file, {
        fileName: file.name,
        parentId,
        conflictStrategy,
        batch,   // #1316 review round 2: as the person who dropped them — the batch's rule, when each file is sent
      })
      promises.push(promise)
    }
    const settled = await Promise.allSettled(promises)
    let successCount = 0
    for (const r of settled) {
      if (r.status === 'fulfilled' && !r.value.skipped) successCount++
    }
    return successCount
  }, [manager])

  /**
   * Full pipeline: scan dropped files → create folder structure → resolve conflicts → hand
   * off to the upload manager. Prep toast surfaces only when there's actual folder work
   * to do (recursion + mkdir API calls); plain file drops skip it.
   */
  const handleCollectedDrop = useCallback(async (
    collected: Awaited<ReturnType<typeof collectDroppedFiles>>,
    targetFolderId: string | null,
    resolveConflict: ConflictResolver,
  ) => {
    // Object holder so TS doesn't narrow to null on the let binding
    // (it can't see the assignment inside the callback closures).
    const prep: { toast: ProgressController | null } = { toast: null }
    // #1316 review round 2 (owner, 2026-10-28): the upload the person started FINISHES as the identity that started it
    // (folders, conflict checks and every file); what it SHOWS stops with its session
    const batch = pinnedBatch()

    let result: Awaited<ReturnType<typeof processCollectedDrop>>
    try {
      result = await processCollectedDrop(collected, {
        targetFolderId,
        resolveConflict,
        batch,
        onScanComplete: (_filesFound, hasDirectories) => {
          if (hasDirectories && !batch.ended()) {
            prep.toast = toast.progress('Creating folders...', 0)
          }
        },
        onFolderProgress: (created, total) => {
          if (!batch.ended()) prep.toast?.update(created, `Creating folders (${created}/${total})...`, total)
        },
      })
    } catch (err) {
      if (!batch.ended()) prep.toast?.fail(err instanceof Error ? err.message : 'Failed to process drop')
      return
    }
    if (!batch.ended()) prep.toast?.complete()

    if (!result || result.uploads.length === 0) return

    const successCount = await dispatchUploads(result.uploads, batch)
    if (!batch.ended()) options?.onComplete?.(targetFolderId, successCount)
  }, [toast, dispatchUploads, options])

  const handleExternalDrop = useCallback(async (
    e: React.DragEvent,
    targetFolderId: string | null,
    resolveConflict: ConflictResolver,
  ) => {
    // collectDroppedFiles() must run synchronously here (before any await)
    // so its webkitGetAsEntry() pass captures the entries before the browser
    // detaches the DataTransfer. Wrap it so a failed read (e.g. a dropped file
    // deleted mid-drag) surfaces a toast instead of an uncaught rejection.
    let collected: Awaited<ReturnType<typeof collectDroppedFiles>>
    try {
      collected = await collectDroppedFiles(e)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process drop')
      return
    }
    await handleCollectedDrop(collected, targetFolderId, resolveConflict)
  }, [handleCollectedDrop, toast])

  /**
   * Picker pipeline: same as drag-drop, but the source is a FileList from
   * `<input type="file">` (with or without webkitdirectory). When the picker
   * selects a folder, files arrive with webkitRelativePath populated and
   * the prep toast surfaces.
   */
  const handlePickedFiles = useCallback(async (
    fileList: FileList,
    targetFolderId: string | null,
    resolveConflict: ConflictResolver,
  ) => {
    const prep: { toast: ProgressController | null } = { toast: null }
    // #1316 review round 2: as in `handleCollectedDrop` — finishes as its starter, shows nothing after its session
    const batch = pinnedBatch()

    let result: Awaited<ReturnType<typeof processPickedFiles>>
    try {
      result = await processPickedFiles(fileList, {
        targetFolderId,
        resolveConflict,
        batch,
        onScanComplete: (_filesFound, hasDirectories) => {
          if (hasDirectories && !batch.ended()) {
            prep.toast = toast.progress('Creating folders...', 0)
          }
        },
        onFolderProgress: (created, total) => {
          if (!batch.ended()) prep.toast?.update(created, `Creating folders (${created}/${total})...`, total)
        },
      })
    } catch (err) {
      if (!batch.ended()) prep.toast?.fail(err instanceof Error ? err.message : 'Failed to prepare upload')
      return
    }
    if (!batch.ended()) prep.toast?.complete()

    if (!result || result.uploads.length === 0) return

    const successCount = await dispatchUploads(result.uploads, batch)
    if (!batch.ended()) options?.onComplete?.(targetFolderId, successCount)
  }, [toast, dispatchUploads, options])

  return { handleExternalDrop, handleCollectedDrop, handlePickedFiles }
}
